import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { EditorState, convertToRaw } from 'draft-js';
import { nanoid } from 'nanoid';
import React, {
  ClipboardEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useAppContext } from '~/App';
import { Input } from '~/components/form/TextField';
import { TaskInput } from '~/generated/graphql';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import { TaskIcon } from '../JobView/TaskList/TaskListItem';
import { type TemplateOption, UseTemplateButton } from '../UseTemplateButton';
import { getTemplateBehavior } from '../AssetDrivenTemplateIndicator';
import { TaskList } from './TaskList';
import { taskTemplateToTaskInput } from './taskTemplateToTaskInput';

export { taskTemplateToTaskInput } from './taskTemplateToTaskInput';

type TaskTemplate = JobTemplateFieldsFragment['tasks'][number];

function randomizeId(task: TaskInput): TaskInput {
  return { ...task, id: nanoid() };
}

function createTask(
  name: string,
  type: string,
  templateBehavior?: 'static' | 'asset_rules'
) {
  const editorState = EditorState.createEmpty();
  const defaultDescription = convertToRaw(editorState.getCurrentContent());

  const task: TaskInput = {
    id: nanoid(),
    type: type,
    name,
    description: JSON.stringify(defaultDescription),
  };

  // For asset-driven templates, create Asset tasks with selection: 'all'
  // BUT only set assetTypes to undefined for the automatically created default task
  // User-created tasks should start with empty array so they can select types
  if (type === 'Asset' && templateBehavior === 'asset_rules') {
    task.asset = {
      selection: 'all',
      assetTypes: [], // Empty array - user will select types (not undefined = default rule)
      allow: ['edit'],
    };
  }

  return task;
}

type Props = {
  state: [TaskInput[], Dispatch<SetStateAction<TaskInput[]>>];
  onShowTask: (id: string) => void;
  onChange?: () => void;
  onDeleteTask?: (id: string) => void;
  onTaskTypeUpdate: (id: string, type: string) => void;
  label?: string;
  placeholder?: string;
  isEditMode?: boolean;
  isNewTask?: (id: string) => boolean;
  isTaskEditable?: (task: TaskInput) => boolean;
  /** Restrict task types to only these types (e.g., ['Asset'] for asset-based templates) */
  allowedTaskTypes?: string[];
  /** Default task type when creating new tasks */
  defaultTaskType?: string;
  /** Template behavior type - 'asset_rules' for rules-based templates */
  templateBehavior?: 'static' | 'asset_rules';
};

export function ManageTasks({
  state,
  onShowTask,
  onChange,
  onDeleteTask,
  onTaskTypeUpdate,
  label = 'Tasks',
  placeholder = 'Task Name',
  isEditMode = false,
  isTaskEditable,
  allowedTaskTypes,
  defaultTaskType,
  templateBehavior,
}: Props) {
  const [tasks, setTasks] = state;

  const [taskInput, setTaskInput] = useState<string>('');
  // Ensure defaultTaskType aligns with allowedTaskTypes if provided
  // If defaultTaskType is not provided but allowedTaskTypes is, use the first allowed type
  const initialTaskType = useMemo(() => {
    if (defaultTaskType) {
      return defaultTaskType;
    }
    if (allowedTaskTypes && allowedTaskTypes.length > 0) {
      return allowedTaskTypes[0];
    }
    return 'Standard';
  }, [defaultTaskType, allowedTaskTypes]);
  const [taskType, setTaskType] = useState<string>(initialTaskType);
  const [pastedTasks, setPastedTasks] = useState<string[] | null>(null);

  // Keep taskType in sync when initialTaskType changes (e.g., when defaultTaskType or allowedTaskTypes change)
  useEffect(() => {
    setTaskType(initialTaskType);
  }, [initialTaskType]);

  // Memoize filtered tasks to prevent recalculations on every render
  const visibleTasks = useMemo(
    () => tasks.filter((task) => !task._destroy),
    [tasks]
  );

  // Memoize callback functions
  const quickAdd = useCallback(() => {
    if (pastedTasks) {
      pastedTasks.forEach((name) => {
        name !== '' &&
          name !== '\n' &&
          setTasks((state) => [
            ...state,
            createTask(name, taskType, templateBehavior),
          ]);
      });
      onChange && onChange();
      setPastedTasks(null);
      setTaskInput('');
    } else if (taskInput) {
      setTasks((state) => [
        ...state,
        createTask(taskInput, taskType, templateBehavior),
      ]);
      onChange && onChange();
      setTaskInput('');
    }
  }, [pastedTasks, taskInput, taskType, templateBehavior, setTasks, onChange]);

  function parsePasted(e: ClipboardEvent<HTMLInputElement>) {
    const value = e.clipboardData?.getData('Text') ?? '';
    const textArray = value.split(/^/gm);
    setPastedTasks(textArray);
  }

  const deleteTask = useCallback(
    (id: string) => {
      if (onDeleteTask) {
        onDeleteTask(id);
        return;
      }

      // Use functional update so this handler does not depend on the outer `tasks` array.
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== id));
    },
    [onDeleteTask, setTasks]
  );

  const focusTaskNameInput = () => {
    const taskNameInputEl = document
      .getElementById('task-name-field')
      ?.querySelector('input');

    if (taskNameInputEl) setTimeout(() => taskNameInputEl.focus(), 0);
  };

  return (
    <>
      {label && (
        <div className='mb-2 ml-1 flex items-end justify-between text-sm font-medium text-grey-50'>
          {label}
        </div>
      )}

      <TaskList
        tasks={visibleTasks}
        onClick={onShowTask}
        onSort={setTasks}
        onDelete={deleteTask}
        onTaskTypeUpdate={onTaskTypeUpdate}
        isEditMode={isEditMode}
        isTaskTypeEditable={isTaskEditable}
        templateBehavior={templateBehavior}
      />

      <div className='mb-2 flex items-center gap-3'>
        <div className='relative flex-1'>
          <Input
            onChange={(event: any) => {
              setTaskInput(event.target.value);
            }}
            onPaste={(event) => parsePasted(event)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Ignore Enter key during IME composition to allow proper text input
                if (e.nativeEvent.isComposing) return;
                e.preventDefault();
                e.stopPropagation();
                quickAdd();
              }
            }}
            className='mb-0'
            id='task-name-field'
            label=''
            value={taskInput}
            placeholder={placeholder}
            icon={
              <TaskTypesSelect
                type={taskType}
                onTypeSelect={(type) => {
                  setTaskType(type);
                  focusTaskNameInput();
                }}
                canEditTaskType={true}
                allowedTaskTypes={allowedTaskTypes}
              />
            }
          />
        </div>
        {taskInput && (
          <button
            type='button'
            onClick={(e) => quickAdd()}
            className='z-20 flex h-8 w-8 items-center justify-center rounded-full bg-[#919FB4] p-1.5 transition-all duration-300 hover:bg-[#8894A9]'
          >
            <PlusIcon className='h-full w-full text-white' />
          </button>
        )}
      </div>
    </>
  );
}

/**
 * Custom hook to get available task type options based on user abilities.
 * Must be called at component top level (not inside callbacks or conditions).
 */
export const useTaskTypeOptions = (): { value: string; label: string }[] => {
  const { ability } = useAppContext();

  return useMemo(() => {
    const options = [{ value: 'Standard', label: 'Standard' }];

    // Defensive check for ability being undefined
    if (!ability) {
      return options;
    }

    if (ability.can('use', 'feat.tasks.attribute')) {
      options.push({ value: 'Attribute', label: 'Attributes Audit' });
      // Stock features disabled: not in use at the moment. Keep for backward compatibility in read/perform mode.
      // options.push({ value: 'Stocktake', label: 'Stocktake' });
      options.push({ value: 'Asset', label: 'Asset' });
    } else if (ability.can('use', 'feat.tasks.advanced')) {
      options.push({ value: 'Attribute', label: 'Attributes Audit' });
      // Stock features disabled: not in use at the moment.
      // options.push({ value: 'Purchase', label: 'Purchases' });
      // options.push({ value: 'Stocktake', label: 'Stocktake' });
      // options.push({ value: 'Restock', label: 'Restock' });
      // options.push({ value: 'Transfer Stock', label: 'Transfer Stock' });
      options.push({ value: 'Asset', label: 'Asset' });
    }

    return options;
  }, [ability]);
};

type TaskTypesSelectProps = {
  type: string;
  onTypeSelect: (type: string) => void;
  canEditTaskType?: boolean;
  /** Restrict task types to only these types (e.g., ['Asset'] for asset-based templates) */
  allowedTaskTypes?: string[];
  /** Whether this is a default rule task (for asset-driven templates) */
  isDefaultRuleTask?: boolean;
};

const TaskTypesSelectComponent = ({
  type,
  onTypeSelect,
  canEditTaskType = true,
  allowedTaskTypes,
  isDefaultRuleTask = false,
}: TaskTypesSelectProps) => {
  const allTypeOptions = useTaskTypeOptions();
  // Filter type options if allowedTaskTypes is provided
  const typeOptions = useMemo(() => {
    if (allowedTaskTypes && allowedTaskTypes.length > 0) {
      return allTypeOptions.filter((option) =>
        allowedTaskTypes.includes(option.value)
      );
    }
    return allTypeOptions;
  }, [allTypeOptions, allowedTaskTypes]);

  if (
    process.env.NODE_ENV === 'development' &&
    (globalThis as any).__DEBUG_TASK_RENDERS__
  ) {
    console.count('TaskTypesSelect render');
  }

  if (typeOptions.length <= 1) return null;

  return (
    <Popover>
      {({ open, close }) => {
        return (
          <div className='flex w-full flex-row overflow-x-auto'>
            <PopoverButton disabled={!canEditTaskType} className='outline-none'>
              <TaskTypeTooltip
                tooltipText={
                  canEditTaskType
                    ? `${type} Task`
                    : isDefaultRuleTask
                    ? `${type} Task - Type cannot be changed for the default rule task`
                    : `${type} Task - Type cannot be changed for existing Complex Tasks`
                }
                isTaskTypesSelectOpen={open}
              >
                <div
                  className={classNames(
                    canEditTaskType
                      ? {
                          'bg-[#004B4B1F] text-brand': open,
                          'text-tertiary hover:bg-[#004B4B14]': !open,
                        }
                      : 'text-tertiary hover:text-secondary',
                    'z-20 flex h-9 w-9 items-center justify-center rounded-full p-1 transition-all duration-200 hover:text-brand'
                  )}
                >
                  <TaskIcon taskType={type} />
                </div>
              </TaskTypeTooltip>
            </PopoverButton>
            <PopoverPanel
              anchor='bottom start'
              className='border-gray-10 absolute z-[100] w-52 rounded-sm border bg-white p-1 pb-1 shadow-md'
            >
              <div className='flex flex-col'>
                {typeOptions.map((taskType, idx) => {
                  const isActive = type === taskType.value;
                  return (
                    <div
                      key={idx}
                      className={classNames(
                        'flex cursor-pointer items-center gap-1 px-3.5 py-2 hover:bg-[#00707014]',
                        {
                          'bg-[#00707014] text-brand': isActive,
                        }
                      )}
                      onClick={() => {
                        onTypeSelect(taskType.value);
                        close();
                      }}
                    >
                      <span
                        className={classNames(
                          'flex-1 truncate whitespace-nowrap',
                          {
                            'text-brand': isActive,
                            'text-primary': !isActive,
                          }
                        )}
                      >
                        {taskType.label}
                      </span>
                      <div
                        className={classNames(
                          'flex h-5 items-center justify-end',
                          {
                            'text-brand': isActive,
                            'text-tertiary': !isActive,
                          }
                        )}
                      >
                        <TaskIcon taskType={taskType.value} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </PopoverPanel>
          </div>
        );
      }}
    </Popover>
  );
};

export const TaskTypesSelect = React.memo(
  TaskTypesSelectComponent,
  (prev, next) => {
    // Compare allowedTaskTypes arrays - check if they're the same reference or have same contents
    const prevAllowed = prev.allowedTaskTypes;
    const nextAllowed = next.allowedTaskTypes;
    const allowedTaskTypesEqual =
      prevAllowed === nextAllowed ||
      (prevAllowed &&
        nextAllowed &&
        prevAllowed.length === nextAllowed.length &&
        prevAllowed.every((val, idx) => val === nextAllowed[idx]));

    return (
      prev.type === next.type &&
      prev.canEditTaskType === next.canEditTaskType &&
      prev.onTypeSelect === next.onTypeSelect &&
      prev.isDefaultRuleTask === next.isDefaultRuleTask &&
      Boolean(allowedTaskTypesEqual)
    );
  }
);

function TaskTypeTooltip({
  children,
  tooltipText,
  isTaskTypesSelectOpen = false,
}: {
  children: React.ReactNode;
  tooltipText: string;
  isTaskTypesSelectOpen?: boolean;
}) {
  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const [show, setShow] = useState(false);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top-end',
    modifiers: [
      {
        name: 'flip',
        enabled: false,
      },
    ],
  });

  return (
    <>
      <span ref={setReferenceElement}>
        <div
          className='relative rounded-full'
          onTouchEnd={() => setShow(true)}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          {children}
        </div>
      </span>

      {show &&
        !isTaskTypesSelectOpen &&
        createPortal(
          <div
            ref={setPopperElement}
            style={{ ...styles.popper, zIndex: 55 }}
            {...attributes.popper}
          >
            <div className='z-20 my-1 max-w-xs rounded bg-[#2D3131] px-2 py-1 text-xs text-[#EEF1F1]'>
              {tooltipText}
            </div>
          </div>,
          document.querySelector('#tooltip')!
        )}
    </>
  );
}

export function UseTemplate({
  taskState,
  onUseTemplateSelect,
  jobTemplates,
}: {
  jobTemplates: readonly JobTemplateFieldsFragment[];
  taskState: [TaskInput[], Dispatch<SetStateAction<TaskInput[]>>];
  onUseTemplateSelect?: (template: JobTemplateFieldsFragment) => void;
}) {
  const [, setTasks] = taskState;

  // Build template options with behavior information for visual differentiation
  const templateOptions: TemplateOption[] = useMemo(
    () =>
      jobTemplates.map((template) => ({
        value: template.id,
        label: template.name,
        behavior: getTemplateBehavior(template),
      })),
    [jobTemplates]
  );

  const handleSelectTemplate = (value: string) => {
    const template = jobTemplates.find(({ id }) => id === value);

    if (!template) return;

    // For asset-driven templates, don't apply tasks immediately
    // The parent component will handle asset selection first
    const isAssetDriven = getTemplateBehavior(template) === 'asset_rules';

    if (!isAssetDriven) {
      // Standard template: apply tasks directly
      const templateTasks: TaskTemplate[] = template.tasks ?? [];
      setTasks((currentTasks) => [
        // Keep any tasks that have been marked for deletion
        ...currentTasks.filter(({ _destroy }) => _destroy === true),
        ...templateTasks.map(taskTemplateToTaskInput).map(randomizeId),
      ]);
    }

    // Always notify parent of template selection
    if (onUseTemplateSelect) {
      onUseTemplateSelect(template);
    }
  };

  return (
    <div className='mb-3 ml-1'>
      <UseTemplateButton
        templateOptions={templateOptions}
        onSelect={handleSelectTemplate}
      />
    </div>
  );
}
